import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import * as progressCloud from "../utils/progressCloud";
import { subscribeProgressSaved } from "../utils/progressEvents";
import { ACTIVITY_FEATURES, ActivityId, lastActiveByActivity, summarizeRecentActivity } from "../utils/activitySummary";

type LastActive = Partial<Record<ActivityId, Date>>;
type State = { identity?: string; status: "loading" | "ready" | "error"; lastActive: LastActive };

// How often the "today"/"yesterday"/exact-day-count classification is
// re-evaluated against the real wall clock while the tab just sits open -
// not because any progress actually changed, only because time passed
// (e.g. a learner leaves the dashboard tab open across midnight). Coarse on
// purpose, since this only flips a calendar-day boundary.
const RECHECK_INTERVAL_MS = 60000;

export function useRecentActivity(now?: Date) {
    const { status: authStatus, user } = useAuth();
    const identity = authStatus === "authenticated" ? user?.identityId : undefined;
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<State>({ status: "loading", lastActive: {} });

    useEffect(() => subscribeProgressSaved((change) => {
        if (change.userId === identity && ACTIVITY_FEATURES.includes(change.feature)) {
            setAttempt((value) => value + 1);
        }
    }), [identity]);

    useEffect(() => {
        if (!identity) return undefined;
        let cancelled = false;
        setState((current) => (current.identity === identity
            ? current
            : { identity, status: "loading", lastActive: {} }));
        const request = typeof progressCloud.getCloudProgressBatch === "function"
            ? progressCloud.getCloudProgressBatch(identity, ACTIVITY_FEATURES)
            : Promise.reject(new Error("Batch progress reads are unavailable."));
        request
            .then((rows) => {
                if (!cancelled) setState({ identity, status: "ready", lastActive: lastActiveByActivity(rows) });
            })
            .catch(() => {
                if (cancelled) return;
                setState((current) => (current.identity === identity && current.status === "ready"
                    ? current
                    : { identity, status: "error", lastActive: {} }));
            });
        return () => {
            cancelled = true;
        };
    }, [identity, attempt]);

    // A caller-supplied `now` (tests) is a fixed clock, so it never needs
    // live re-checks. Otherwise re-evaluate when the tab regains focus or
    // visibility, and on a coarse interval, so a tab left open across a
    // calendar-day boundary doesn't keep showing a stale "today"/"yesterday"
    // classification until the next cloud fetch happens to run.
    const [recheckCount, setRecheckCount] = useState(0);
    useEffect(() => {
        if (now) return undefined;
        const recheck = () => setRecheckCount((value) => value + 1);
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") recheck();
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", recheck);
        const interval = setInterval(recheck, RECHECK_INTERVAL_MS);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", recheck);
            clearInterval(interval);
        };
    }, [now]);

    const visible = Boolean(identity) && state.identity === identity;
    const status = !identity ? "signed-out" : visible ? state.status : "loading";
    const lastActive = visible ? state.lastActive : null;
    const nowTime = now?.getTime();
    const summary = useMemo(() => {
        if (!lastActive) return null;
        try {
            return summarizeRecentActivity(lastActive, nowTime === undefined ? new Date() : new Date(nowTime));
        } catch {
            return null;
        }
        // recheckCount isn't read above - including it just forces this memo
        // to recompute against a fresh `new Date()` each time it changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastActive, nowTime, recheckCount]);

    return { status, summary };
}
